import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Moon, Sun, LogOut, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import VerifyEmailBanner from "@/components/VerifyEmailBanner";
import AskYumo from "@/components/AskYumo";

const linkBase = "text-sm font-medium transition-colors hover:text-foreground";
const linkInactive = "text-muted-foreground";
const linkActive = "text-foreground";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  const primaryItems = user
    ? [
        { to: "/dashboard", label: "Dashboard" },
        { to: "/recommend", label: "Discover" },
        { to: "/explorer", label: "Explore" },
        { to: "/profile", label: "Profile" },
      ]
    : [];

  const moreItems = user
    ? [
        { to: "/learn", label: "Learn YUMO" },
        { to: "/community", label: "Community", premium: true },
        { to: "/vrat", label: "Vrat Intelligence", premium: true },
        { to: "/enterprise", label: "Enterprise", premium: true },
        { to: "/analytics", label: "Analytics" },
        ...(user.role === "admin" ? [{ to: "/admin", label: "Admin" }] : []),
      ]
    : [];

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border sticky top-0 z-30 bg-background/80 backdrop-blur-md" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 h-16 sm:h-[68px] flex items-center justify-between gap-3 overflow-visible">
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2 shrink-0 min-w-0 py-1.5" data-testid="brand-link">
            <img
              src="/brand/yumo-wordmark.png"
              alt="YUMO"
              className="h-8 sm:h-9 md:h-10 w-auto shrink-0 object-contain"
              style={{ maxHeight: "44px" }}
              draggable="false"
            />
            <span className="hidden lg:inline overline shrink-0 whitespace-nowrap">/ Food Intelligence OS</span>
          </Link>

          <nav className="hidden md:flex items-center gap-4 lg:gap-6 xl:gap-8 min-w-0">
            {primaryItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive} whitespace-nowrap`}
                data-testid={`nav-${it.label.toLowerCase()}`}
              >
                {it.label}
              </NavLink>
            ))}
            {user && moreItems.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setMoreOpen((o) => !o)}
                  onBlur={() => setTimeout(() => setMoreOpen(false), 150)}
                  className={`${linkBase} ${linkInactive} flex items-center gap-1 whitespace-nowrap`}
                  data-testid="nav-more"
                >
                  More <MoreHorizontal className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
                {moreOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-xl border border-border bg-card shadow-lg py-2 z-50" data-testid="nav-more-menu">
                    {moreItems.map((it) => (
                      <Link
                        key={it.to}
                        to={it.to}
                        className="flex items-center justify-between px-4 py-2 text-sm hover:bg-secondary/60 transition"
                        data-testid={`nav-more-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <span>{it.label}</span>
                        {it.premium && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary uppercase tracking-widest">Premium</span>}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={toggle}
              className="h-9 w-9 rounded-full border border-border flex items-center justify-center hover:bg-secondary transition"
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" strokeWidth={1.5} /> : <Moon className="w-4 h-4" strokeWidth={1.5} />}
            </button>
            {user ? (
              <Button onClick={handleLogout} variant="ghost" size="sm" data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-1" strokeWidth={1.5} /> Logout
              </Button>
            ) : (
              <div className="flex gap-2">
                <Link to="/login"><Button variant="ghost" size="sm" data-testid="header-login-button">Login</Button></Link>
                <Link to="/signup"><Button size="sm" data-testid="header-signup-button">Get Started</Button></Link>
              </div>
            )}
          </div>
        </div>
      </header>

      <VerifyEmailBanner />

      {/* Mobile nav */}
      {user && (
        <nav className="md:hidden border-b border-border bg-background overflow-x-auto">
          <div className="flex gap-5 px-6 py-2 whitespace-nowrap">
            {[...primaryItems, ...moreItems].map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive} flex items-center gap-1`}
                data-testid={`mnav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {it.label}
                {it.premium && <span className="text-[8px] px-1 py-0.5 rounded bg-primary/10 text-primary uppercase">P</span>}
              </NavLink>
            ))}
          </div>
        </nav>
      )}

      <main>{children}</main>

      <footer className="mt-24 border-t border-border bg-card/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-5">
              <img
                src="/brand/yumo-logo.png"
                alt="YUMO — Yummy Food For Your Mood"
                className="h-12 md:h-14 w-auto"
                draggable="false"
              />
              <div className="text-sm text-muted-foreground mt-3">Yummy Food For Your Mood</div>
              <p className="mt-4 text-sm text-foreground/75 max-w-sm leading-relaxed">
                Made with love by foodies who care about better food decisions.
              </p>
            </div>
            <div className="md:col-span-4">
              <div className="overline mb-3">Explore</div>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="text-foreground/85 hover:text-primary transition" data-testid="footer-home-link">Home</Link></li>
                <li><Link to="/about" className="text-foreground/85 hover:text-primary transition" data-testid="footer-about-link">About Us</Link></li>
                <li><Link to="/contact" className="text-foreground/85 hover:text-primary transition" data-testid="footer-contact-link">Contact Us</Link></li>
              </ul>
            </div>
            <div className="md:col-span-3">
              <div className="overline mb-3">Legal</div>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="text-foreground/85 hover:text-primary transition" data-testid="footer-privacy-link">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-foreground/85 hover:text-primary transition" data-testid="footer-terms-link">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-muted-foreground">
            <div>© 2026 YUMO · Food Intelligence Operating System</div>
            <div className="overline">v1.0 · Mother Drive Engine</div>
          </div>
        </div>
      </footer>
      <AskYumo />
    </div>
  );
}
